
import { useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';

interface RedirectMapping {
  param: string;
  value: string;
  url: string;
  fullPath?: string;
}

interface ExternalRedirectProps {
  paramName: string;
  redirectMappings: RedirectMapping[];
  children: React.ReactNode;
  immediate?: boolean;
  checkFullPath?: boolean;
}

/**
 * Component that handles external redirects based on URL parameters or full path matching
 */
const ExternalRedirect = ({ 
  paramName, 
  redirectMappings, 
  children, 
  immediate = false,
  checkFullPath = false
}: ExternalRedirectProps) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  // The specific pattern we need to handle - don't depend on specific domain
  const targetPattern = '21d11b63-7b91-46fa-8c91-a30744574195';
  const redirectDestination = 'https://www.omooduduwa.org/join';
  
  useEffect(() => {
    // Immediate execution for highest priority
    const handleRedirect = () => {
      // Get the current URL and referrer parameter
      const currentUrl = window.location.href;
      const pathName = window.location.pathname;
      const referrerParam = searchParams.get('referrer');
      
      console.log("ExternalRedirect checking URL:", currentUrl);
      console.log("ExternalRedirect checking path:", pathName);
      
      // HIGHEST PRIORITY: Check for our specific pattern in URL or referrer parameter
      if (currentUrl.includes(targetPattern) || referrerParam === targetPattern) {
        console.log("DIRECT MATCH FOUND - Redirecting to:", redirectDestination);
        window.location.replace(redirectDestination);
        return true; // Indicate redirect was performed
      }
      
      // Additional check: If we're on the /join page with the special referrer
      if (pathName === '/join' && referrerParam === targetPattern) {
        console.log("JOIN PAGE WITH SPECIAL REFERRER - Redirecting to:", redirectDestination);
        window.location.replace(redirectDestination);
        return true;
      }
      
      // SECOND PRIORITY: Check for full path matches
      if (checkFullPath) {
        // Modified to be more flexible - check any domain with the join path and our ID
        const fullPathMapping = redirectMappings.find(m => {
          if (!m.fullPath) return false;
          
          // Check if current path is /join AND we have our special ID
          const isJoinPath = pathName === '/join' || currentUrl.includes('/join');
          const hasTargetPattern = currentUrl.includes(targetPattern) || 
                                  referrerParam === targetPattern;
                                  
          return isJoinPath && hasTargetPattern;
        });
        
        if (fullPathMapping) {
          console.log("Full path match found. Redirecting to:", fullPathMapping.url);
          window.location.replace(fullPathMapping.url);
          return true; // Indicate redirect was performed
        }
      }
      
      // LOWEST PRIORITY: Check for parameter-based redirects
      const paramValue = searchParams.get(paramName);
      if (paramValue) {
        const mapping = redirectMappings.find(m => 
          m.param === paramName && m.value === paramValue
        );
        
        if (mapping) {
          console.log("Parameter match found. Redirecting to:", mapping.url);
          window.location.replace(mapping.url);
          return true; // Indicate redirect was performed
        }
      }
      
      return false; // No redirect was performed
    };
    
    // Execute redirect check immediately
    handleRedirect();
    
    // Also set up a brief interval to keep checking (catches more edge cases)
    const checkInterval = setInterval(() => {
      const redirected = handleRedirect();
      if (redirected) {
        clearInterval(checkInterval);
      }
    }, 1000); // Check every second
    
    return () => clearInterval(checkInterval);
  }, [searchParams, paramName, redirectMappings, immediate, checkFullPath, location]);
  
  return <>{children}</>;
};

export default ExternalRedirect;
